import { useCallback, useMemo, useReducer } from 'react'
import axios from 'axios'
import debounce from 'lodash/debounce'
import groupBy from 'lodash/groupBy'
import isEmpty from 'lodash/isEmpty'
import { useDispatch } from 'react-redux'
import { useDeepCompareEffect } from 'react-use'
import { useSearchParamsContext } from '@contexts'
import {
  extractSearchFromParams,
  generateFilterParam,
  generateSearchParam,
  generateSortParam,
  getInfoFromParams,
} from '@contexts/searchParamsContext/utils'
import { useDatasetPendingRowsQuery } from '@global/hooks/queries/useDatasetPendingRowsObserver'
import * as SnackbarActions from '@global/snackbar/snackbar.actions'
import { ColumnDataType } from '@global/types'
import { makeQueryErrorMessage } from '@global/utils/reactQuery'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import * as DatasetsActions from '../datasets.actions'
import { useApprovalSettings } from './hooks/useApprovalSettings'
import { useDatasetsQuery } from './hooks/useDatasetsQuery'
import { reducer, initialState } from './reducer'
import { ActionType } from './types/context'
import { makeQueryOptionsDto } from './utils'
import { QueryOptionFilter, ColumnQueryOptions, IColumnQueryOptions } from './utils/queryOptions'
import { SubmissionPartial, useSubmissionPartials } from '../hooks/useSubmissionPartials'
import { trackEvent } from 'lib/eventTracking'
import { EventTrackingEventType } from 'lib/eventTracking'

import { DATASET_QUERY_LIMIT } from './constants'
import { DatasetQueryContext } from './context'
import { DatasetQueryContextValue } from './types'

export const DatasetQueryContextProvider = ({
  children,
  datasetId,
}: {
  children: React.ReactNode
  datasetId: string
}) => {
  const [state, reducerDispatch] = useReducer(reducer, initialState)
  const {
    commitRef,
    datasetRowCount,
    datasetLoading,
    limit,
    offset,
    showHiddenColumns,
    queryOptionsByFieldId,
    queryRowCountIsLoading,
    queryRowCount,
    updatingFilters,
  } = state

  const { isRequiringApproval } = useApprovalSettings()
  const dispatch = useDispatch()
  const { params, updateParams } = useSearchParamsContext()
  const datasetMetadata = useAppSelector(state => state.Datasets.datasetMetadataById[datasetId])
  const templateId = datasetMetadata?.template_id

  const { refetchDataset } = useDatasetsQuery({
    datasetId,
    reducerDispatch,
    queryOptionsByFieldId,
    limit: DATASET_QUERY_LIMIT,
    offset,
    commitRef,
    showHiddenColumns,
    templateId,
  })

  const { data: submissionPartials = {}, isInitialLoading: submissionPartialsLoading = false } =
    useSubmissionPartials(templateId)

  const { isLoading: pendingRowsIsLoading } = useDatasetPendingRowsQuery({ datasetId })

  useDeepCompareEffect(() => {
    if (datasetLoading) return
    if (isEmpty(params)) return
    if (submissionPartialsLoading || !submissionPartials) return

    setStateFromQuery(params, submissionPartials)
  }, [datasetLoading, params, submissionPartials, submissionPartialsLoading])

  const getAllDatasetData = useCallback(
    async ({ dsId }) => {
      const requestSource = axios.CancelToken.source()
      const queryConfig = {
        cancelToken: requestSource.token,
      }

      const queryOptions = makeQueryOptionsDto({
        optionsByFieldId: queryOptionsByFieldId,
        queryCommitRef: commitRef,
        includeComments: isRequiringApproval,
        includeRowProperties: true,
      })

      try {
        const { data } = await api.post(
          `/api/core/v1/datasets/${dsId}/query`,
          queryOptions,
          queryConfig,
        )
        return data
      } catch (error) {
        const message = makeQueryErrorMessage(error as Error)

        if (message) {
          dispatch(SnackbarActions.showSnackbar({ type: 'ERROR', message }))
        }
      }
      return null
    },
    [queryOptionsByFieldId, commitRef, isRequiringApproval, dispatch],
  )

  const isLoading =
    datasetLoading || updatingFilters || submissionPartialsLoading || pendingRowsIsLoading

  function updateQueryOptionsByFieldId(fieldId: string | null, queryOptions) {
    // Reset the offset to 0 anytime the query options change. Why?
    // 1) Ensures the query will fetch the right data
    // 2) Ensures the UI scrolls back to the top of the view
    reducerDispatch({ type: ActionType.setOffset, payload: 0 })

    if (fieldId === null) {
      reducerDispatch({ type: 'resetFilters' })

      return {}
    }

    const oldQueryOptions = queryOptionsByFieldId[fieldId] ?? new ColumnQueryOptions()

    const nextQueryOptionsByFieldId = {
      [fieldId]: {
        ...oldQueryOptions,
        ...queryOptions,
      },
    }

    trackEvent(EventTrackingEventType.UPDATE_QUERY_OPTIONS_BY_FIELD_ID, {
      fieldId,
      nextQueryOptionsByFieldId,
    })

    reducerDispatch({
      type: 'setQueryOptions',
      payload: nextQueryOptionsByFieldId,
    })

    return nextQueryOptionsByFieldId
  }

  const updateUrlParams = useCallback(
    (queryOptions: IColumnQueryOptions) => {
      const filtersForAllFields = Object.values({
        ...queryOptionsByFieldId,
        ...queryOptions,
      }).flatMap(opts => opts.filters)

      const sortOrderForAllFields = Object.entries(queryOptions)
        .filter(([_, opts]) => opts.sortOrder)
        .flatMap(([fieldId, opts]) => {
          return {
            fieldId,
            sort: opts.sortOrder,
          }
        })

      const searchForAllFields = Object.entries(queryOptions)
        .filter(([_, queryOptions]) => queryOptions.searchTerm)
        .flatMap(([fieldId, queryOptions]) => {
          return {
            fieldId,
            searchTerm: queryOptions.searchTerm,
            columnDataType: queryOptions.columnDataType,
            submissionIds: queryOptions.submissionIds,
          }
        })

      updateParams({
        filter:
          filtersForAllFields.length > 0 ? filtersForAllFields.map(generateFilterParam) : undefined,
        sort:
          sortOrderForAllFields.length > 0
            ? sortOrderForAllFields.map(generateSortParam)
            : undefined,
        search:
          searchForAllFields.length > 0 ? searchForAllFields.map(generateSearchParam) : undefined,
      })
    },
    [queryOptionsByFieldId, updateParams],
  )

  // Update filters in query options for a given field ID
  function updateFilters(fieldId, filtersByFieldId) {
    const filtersForField = filtersByFieldId[fieldId]
    if (filtersForField) {
      updateQueryOptionsByFieldId(fieldId, { filters: filtersForField })
    }
  }

  // Update sorting order in query options for a given field ID
  function updateSortOrders(fieldId: string, sortOrders, filtersByFieldId) {
    const sortOrderForField = sortOrders[fieldId]
    if (sortOrderForField) {
      updateQueryOptionsByFieldId(fieldId, {
        filters: filtersByFieldId[fieldId] || [],
        sortOrder: sortOrderForField[0]?.sort || '',
      })
    }
  }

  function setStateFromQuery(currentParams, groupedByName) {
    const { filters, sortOrdersByFieldId } = getInfoFromParams(currentParams, groupedByName)
    const searchByFieldId = extractSearchFromParams(currentParams)
    const filtersByFieldId = Array.isArray(filters)
      ? groupBy(filters, 'fieldId')
      : { [filters.fieldId]: [filters] }

    Object.keys(filtersByFieldId).forEach(fieldId => {
      updateFilters(fieldId, filtersByFieldId)
    })

    if (!isEmpty(sortOrdersByFieldId)) {
      Object.keys(sortOrdersByFieldId).forEach(fieldId => {
        updateSortOrders(fieldId, sortOrdersByFieldId, filtersByFieldId)
      })
    }

    if (!isEmpty(searchByFieldId)) {
      for (const fieldId in searchByFieldId) {
        const filters = filtersByFieldId?.[fieldId] ?? []
        const searchForField = searchByFieldId[fieldId] ?? []
        const searchTerm = searchForField[0]?.search ?? ''
        const columnDataType = searchForField[0]?.columnDataType ?? ''

        let submissionIds: number[] = []

        if (columnDataType === ColumnDataType.SUBMISSION) {
          const submissions = Object.values<SubmissionPartial[]>(groupedByName).flat()
          submissionIds = submissions
            .filter(s => s.submission_name.toLowerCase().includes(searchTerm.toLowerCase()))
            .map(s => s.submission_id)
        }

        updateQueryOptionsByFieldId(fieldId, {
          filters,
          searchTerm,
          columnDataType,
          submissionIds,
        })
      }
    }

    reducerDispatch({ type: 'setUpdatingFilters', payload: false })
  }

  function setDatasetLoading(val) {
    reducerDispatch({ type: 'setDatasetLoading', payload: val })
  }

  const value: DatasetQueryContextValue = useMemo(() => {
    function setQueryOptionsByFieldId(val) {
      reducerDispatch({ type: 'setQueryOptions', payload: val })
    }

    function getQueryOptionsByFieldId(fieldId) {
      return queryOptionsByFieldId[fieldId] || new ColumnQueryOptions()
    }

    function getFiltersByFieldId(fieldId) {
      return getQueryOptionsByFieldId(fieldId).filters
    }

    function getSortOrderByFieldId(fieldId) {
      return getQueryOptionsByFieldId(fieldId).sortOrder
    }

    function getSearchTermByFieldId(fieldId) {
      return getQueryOptionsByFieldId(fieldId).searchTerm
    }

    function hasFilterWithOperator(fieldId, operator) {
      const { filters } = getQueryOptionsByFieldId(fieldId)
      return filters.some(filter => filter.operator === operator)
    }

    function hasQueryOptions(fieldId) {
      const { filters, sortOrder, searchTerm } = getQueryOptionsByFieldId(fieldId)
      return filters.length || sortOrder || searchTerm
    }

    function updateSortOrderByFieldId(fieldId, sortOrder) {
      updateQueryOptionsByFieldId(fieldId, { sortOrder })
    }

    function updateFiltersByFieldId(fieldId, filters) {
      updateQueryOptionsByFieldId(fieldId, { filters })
    }

    function updateFilterOperatorByFieldId(fieldId, filterIndex, filterOperator) {
      const newFilters = [...getFiltersByFieldId(fieldId)]
      newFilters[filterIndex] = {
        ...newFilters[filterIndex],
        operator: filterOperator,
      }
      updateFiltersByFieldId(fieldId, newFilters)
    }

    function updateFilterValueByFieldId(fieldId, filterIndex, filterValue) {
      const newFilters = [...getFiltersByFieldId(fieldId)]
      newFilters[filterIndex] = {
        ...newFilters[filterIndex],
        value: filterValue,
      }
      updateFiltersByFieldId(fieldId, newFilters)
    }

    function addFilter(filterIndex, newFilter = new QueryOptionFilter()) {
      const oldFilters = getFiltersByFieldId(newFilter.fieldId)
      const firstHalf = oldFilters.slice(0, filterIndex)
      const lastHalf = oldFilters.slice(filterIndex)
      const newFilters = [...firstHalf, newFilter, ...lastHalf]
      updateFiltersByFieldId(newFilter.fieldId, newFilters)

      return newFilter
    }

    function appendFilter(filter) {
      const oldFilters = getFiltersByFieldId(filter.fieldId)
      const lastIndex = oldFilters.length - 1
      addFilter(lastIndex, filter)
    }

    function removeFilterByFieldId(fieldId, filterIndex) {
      const newFilters = getFiltersByFieldId(fieldId).filter(
        (_filter, index) => index !== filterIndex,
      )
      updateFiltersByFieldId(fieldId, newFilters)
    }

    function clearQueryOptionsByFieldId(fieldId) {
      updateQueryOptionsByFieldId(fieldId, {
        filters: [],
        sortOrder: '',
        searchTerm: '',
      })
    }

    function updateShowHiddenFields(showHiddenFieldsParam) {
      reducerDispatch({ type: ActionType.setShowHiddenColumns, payload: showHiddenColumns })
      dispatch(DatasetsActions.setShowHiddenColumns(datasetId, showHiddenFieldsParam))
    }

    function getDatasetId() {
      return datasetId
    }

    function setCommitRef(val) {
      reducerDispatch({ type: 'setCommitRef', payload: val })
    }

    const setOffsetDebounced = debounce(newOffset => {
      reducerDispatch({ type: ActionType.setOffset, payload: newOffset })
    }, 100)

    return {
      commitRef,
      datasetLoading: isLoading,
      datasetRowCount,
      limit,
      offset,
      queryOptionsByFieldId,
      queryRowCountIsLoading,
      queryRowCount,
      showHiddenColumns,
      submissionPartials,
      submissionPartialsLoading,
      updatingFilters,
      reducerDispatch,
      refetchDatasetTrigger: 0,
      showHiddenFields: false,
      addFilter,
      appendFilter,
      clearQueryOptionsByFieldId,
      getAllDatasetData,
      getDatasetId,
      getFiltersByFieldId,
      getQueryOptionsByFieldId,
      getSearchTermByFieldId,
      getSortOrderByFieldId,
      hasFilterWithOperator,
      hasQueryOptions,
      makeQueryOptionsDto,
      refetchDataset,
      removeFilterByFieldId,
      setCommitRef,
      setDatasetLoading,
      setOffsetDebounced,
      setQueryOptionsByFieldId,
      updateFilterOperatorByFieldId,
      updateFilterValueByFieldId,
      updateFiltersByFieldId,
      updateQueryOptionsByFieldId,
      updateSortOrderByFieldId,
      updateShowHiddenFields,
      updateUrlParams,
    }
  }, [
    isLoading,
    datasetRowCount,
    limit,
    offset,
    queryOptionsByFieldId,
    queryRowCountIsLoading,
    queryRowCount,
    showHiddenColumns,
    submissionPartialsLoading,
  ])

  return <DatasetQueryContext.Provider value={value}>{children}</DatasetQueryContext.Provider>
}
